import { AppHomeButton } from "@pedagogue/ui-core";
import LogoImg from "../../images/LogoImg";
import React, { useState, useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Mail, ArrowRight, KeyRound, Shield, Monitor, Clock } from "lucide-react";
import { Typography, Button, CustomInput, Loading } from "../../components/common";
import { useToast } from "../../context/CustomToast";
import { supabase } from "../../supabase/supabaseClient";
import { usePartner } from "../../hooks/usePartner";
import CobrandedHeader from "../../components/layout/CobrandedHeader";
import { getReturnUrl } from "../../utils/marketingParams";
import { OtpDeliveryNotice } from "../../components/common/OtpDeliveryNotice";

/**
 * Welcome page - partner entry point for email OTP authentication.
 * URL pattern: /welcome?partner_id=spgs&school_id=...&email=...
 *
 * Flow: user enters email → receives 6-digit code via email → enters code → session established.
 * After successful OTP verification, navigates to /auth/verify for post-auth gating
 * (job title check, onboarding redirect).
 */
const APP_HOME_URL = process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io";

const Welcome: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { partner, isPartnerBranded } = usePartner();

  // Pre-fill email from URL if provided
  const initialEmail = searchParams.get("email") || "";
  const schoolId = searchParams.get("school_id") || "";

  const [email, setEmail] = useState(initialEmail);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  // OTP verification state
  const [submittedEmail, setSubmittedEmail] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const otpInputRef = useRef<HTMLInputElement>(null);

  // Handle cooldown countdown
  useEffect(() => {
    if (cooldownSeconds > 0) {
      const timer = setTimeout(() => {
        setCooldownSeconds(cooldownSeconds - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [cooldownSeconds]);

  // Auto-focus OTP input when code sent
  useEffect(() => {
    if (emailSent && otpInputRef.current) {
      otpInputRef.current.focus();
    }
  }, [emailSent]);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    if (error) {
      setError(null);
    }
  };

  const handleOtpChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 6);
    setOtpCode(value);
    if (verifyError) {
      setVerifyError(null);
    }
  };

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const trimmedEmail = email.trim().toLowerCase();

    if (!trimmedEmail) {
      setError("Please enter your email address");
      return;
    }

    if (!validateEmail(trimmedEmail)) {
      setError("Please enter a valid email address");
      return;
    }

    setIsSubmitting(true);
    setError(null);

    try {
      // Request OTP via Supabase — sends a 6-digit code to the user's email.
      const { error: otpError } = await supabase.auth.signInWithOtp({
        email: trimmedEmail,
        options: {
          shouldCreateUser: false,
          data: {
            partner_id: partner?.id || null,
            school_id: schoolId || null,
          },
        },
      });

      if (otpError) {
        // Handle rate limiting
        if (otpError.message?.includes("rate limit") || otpError.status === 429) {
          setCooldownSeconds(60);
          setError("Too many requests. Please wait before trying again.");
        } else {
          setError(otpError.message || "Failed to send verification code. Please try again.");
        }
        return;
      }

      // Success
      setSubmittedEmail(trimmedEmail);
      setEmailSent(true);
      setOtpCode("");
      setCooldownSeconds(60);
      toast.showToast("Verification code sent! Check your email.", "success");
    } catch {
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleVerifyOtp = async (e: React.FormEvent) => {
    e.preventDefault();

    const code = otpCode.trim();
    if (code.length !== 6) {
      setVerifyError("Please enter the full 6-digit code");
      return;
    }

    setIsVerifying(true);
    setVerifyError(null);

    try {
      const { error: verifyOtpError } = await supabase.auth.verifyOtp({
        email: submittedEmail,
        token: code,
        type: "email",
      });

      if (verifyOtpError) {
        setVerifyError(
          verifyOtpError.message?.includes("expired")
            ? "Code has expired. Please request a new one."
            : "Invalid code. Please check and try again."
        );
        return;
      }

      // Session established — route through /auth/verify for post-auth gating.
      toast.showToast("Signed in successfully!", "success");
      const returnTo = searchParams.get("return_to") || "";
      const verifyUrl = `/auth/verify${returnTo ? `?return_to=${encodeURIComponent(returnTo)}` : ""}`;
      navigate(verifyUrl, { replace: true });
    } catch {
      setVerifyError("An unexpected error occurred. Please try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResend = () => {
    if (cooldownSeconds > 0) {
      return;
    }
    setOtpCode("");
    setVerifyError(null);
    setEmailSent(false);
    handleSubmit({ preventDefault: () => {} } as React.FormEvent);
  };

  // If user already logged in, redirect to stored return_to or /courses
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        navigate(getReturnUrl(), { replace: true });
      }
    };
    checkSession();
  }, [navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Co-branded header */}
      {isPartnerBranded && <CobrandedHeader />}
      <div className="flex items-center gap-2 px-4 py-3">
        <LogoImg />
        <AppHomeButton url={APP_HOME_URL} />
      </div>

      <div className="flex flex-col items-center justify-center min-h-screen px-4 py-12">
        <div className="w-full max-w-md">
          {/* Logo/Branding */}
          <div className="text-center mb-8">
            <Typography variant="h2" weight="bold" className="text-textPrimary mb-2">
              Welcome to Pedagogue
            </Typography>
            <Typography variant="p" className="text-textSecondary">
              {isPartnerBranded
                ? (partner.welcomeSubtitle || `Powered by ${partner.name} partnership`)
                : "Your AI learning experience"}
            </Typography>
          </div>

          {/* Main Card */}
          <div className="bg-white rounded-xl shadow-lg border border-slate-200 overflow-hidden">
            {!emailSent ? (
              // Email Input Form
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <Mail className="text-primary" size={32} />
                  </div>
                  <Typography variant="h4" weight="semibold" className="text-textPrimary mb-2">
                    Sign in with Email
                  </Typography>
                  <Typography variant="p" className="text-textSecondary">
                    Enter your email and we'll send you a verification code to sign in.
                  </Typography>
                </div>

                <CustomInput
                  label="Email Address"
                  name="email"
                  type="email"
                  placeholder="your.email@company.com"
                  value={email}
                  onChange={handleEmailChange}
                  error={!!error}
                  helperText={error || undefined}
                  required
                  autoFocus
                  icon={Mail}
                  className={{
                    label: "text-small font-medium",
                    input: "text-p h-11",
                  }}
                />

                <Button
                  label={isSubmitting ? "Sending..." : "Send Verification Code"}
                  variant="filled"
                  className="w-full bg-primary text-white font-bold h-12"
                  endIcon={isSubmitting ? <Loading /> : <ArrowRight size={20} />}
                  disabled={isSubmitting || !email.trim()}
                  buttonType="submit"
                />

                <div className="text-center">
                  <Typography variant="small" className="text-textSecondary">
                    Already have an account?{" "}
                    <button
                      type="button"
                      onClick={() => { window.location.href = (process.env.REACT_APP_APP_HOME_URL || "https://app.pedagogue.io") + "/login"; }}
                      className="text-primary font-medium hover:underline"
                    >
                      Sign in
                    </button>
                  </Typography>
                </div>
              </form>
            ) : (
              // OTP Code Entry Form
              <form onSubmit={handleVerifyOtp} className="p-6 space-y-6">
                <div className="text-center">
                  <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                    <KeyRound className="text-primary" size={32} />
                  </div>
                  <Typography variant="h4" weight="semibold" className="text-textPrimary mb-2">
                    Enter your verification code
                  </Typography>
                  <Typography variant="p" className="text-textSecondary">
                    We've sent a 6-digit code to{" "}
                    <span className="font-medium text-textPrimary">{submittedEmail}</span>.
                    Enter it below to sign in.
                  </Typography>
                </div>

                <OtpDeliveryNotice />

                <div>
                  <label
                    htmlFor="otp-input"
                    className="block text-small font-medium text-textPrimary mb-1.5"
                  >
                    Verification Code
                  </label>
                  <input
                    ref={otpInputRef}
                    id="otp-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    placeholder="000000"
                    value={otpCode}
                    onChange={handleOtpChange}
                    className={`w-full text-center text-2xl font-mono tracking-[0.5em] h-14 rounded-lg border ${
                      verifyError
                        ? "border-red-400 focus:ring-red-400"
                        : "border-slate-300 focus:ring-primary"
                    } focus:outline-none focus:ring-2 focus:border-transparent`}
                  />
                  {verifyError && (
                    <p className="mt-1.5 text-small text-red-500">{verifyError}</p>
                  )}
                </div>

                <Button
                  label={isVerifying ? "Verifying..." : "Verify Code"}
                  variant="filled"
                  className="w-full bg-primary text-white font-bold h-12"
                  endIcon={isVerifying ? <Loading /> : <ArrowRight size={20} />}
                  disabled={isVerifying || otpCode.length !== 6}
                  buttonType="submit"
                />

                <div className="bg-slate-50 rounded-lg p-4 text-left">
                  <Typography variant="small" className="text-textSecondary">
                    <strong>Didn't receive the code?</strong>
                    <ul className="mt-2 space-y-1 list-disc list-inside">
                      <li>Make sure you entered the correct email</li>
                      <li>Wait a few minutes for the email to arrive</li>
                    </ul>
                  </Typography>
                </div>

                <Button
                  label={
                    cooldownSeconds > 0
                      ? `Resend code in ${cooldownSeconds}s`
                      : "Resend Code"
                  }
                  variant="outline"
                  className="w-full h-11"
                  onClick={handleResend}
                  disabled={cooldownSeconds > 0}
                />
              </form>
            )}
          </div>

          {/* Trust signals */}
          <div className="mt-6 space-y-3">
            <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
              <Monitor className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <Typography variant="small" className="text-textSecondary">
                Browser requirements: For the best experience, please use a recent version of Chrome, Safari, or Edge on a desktop or laptop. Mobile phones and tablets are not recommended at the current time.
              </Typography>
            </div>
            <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
              <Shield className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <Typography variant="small" className="text-textSecondary">
                Pedagogue is for staff professional development. We process your account details and learning activity to provide the service. Do not enter personal or sensitive information about pupils or families.
              </Typography>
            </div>
            <div className="flex items-start gap-2 bg-slate-50 rounded-lg p-3 border border-slate-100">
              <Clock className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
              <Typography variant="small" className="text-textSecondary">
                Your verification code may take a few minutes to arrive, depending on your school's email security settings.
              </Typography>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-4 text-center">
            <Typography variant="small" className="text-textSecondary">
              By signing in, you agree to our{" "}
              <a href="https://pedagogue.io/terms-of-service/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                Terms of Service
              </a>{" "}
              and{" "}
              <a href="https://pedagogue.io/privacy-policy/" className="text-primary hover:underline" target="_blank" rel="noopener noreferrer">
                Privacy Policy
              </a>
              .
            </Typography>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Welcome;
